import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { StatusBadge, PaymentBadge } from '@/components/base/StatusBadge';
import { theme } from '@/lib/theme';
import CompleteServiceModal from './CompleteServiceModal';

interface Props {
  appointment: AppointmentRow | null;
  onClose: () => void;
  onCancel?: (id: string) => Promise<boolean>;
  onNoShow?: (id: string) => Promise<boolean>;
  onComplete?: (id: string, method: 'cash' | 'transfer') => Promise<{ ok: boolean; error?: string }>;
  onConfirm?: (id: string) => Promise<boolean>;
  cancelling?: string | null;
  completing?: string | null;
}

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatTime(timeStr: string) {
  if (!timeStr) return '—';
  return timeStr.slice(0, 5);
}

export default function AppointmentDetailModal({
  appointment,
  onClose,
  onCancel,
  onNoShow,
  onComplete,
  onConfirm,
  cancelling,
  completing,
}: Props) {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!appointment) return null;

  const isFinalStatus = ['no_show', 'cancelled', 'completed'].includes(appointment.status);

  const handleNoShow = async () => {
    if (!onNoShow) return;
    const ok = await onNoShow(appointment.id);
    if (ok) onClose();
  };

  const handleConfirmManual = async () => {
    if (!onConfirm) return;
    setConfirming(true);
    const ok = await onConfirm(appointment.id);
    setConfirming(false);
    if (ok) onClose();
  };

  const handleCopyContact = () => {
    const text = [appointment.client_name, appointment.client_email, appointment.client_phone].filter(Boolean).join(' | ');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCompleteConfirm = async (method: 'cash' | 'transfer') => {
    if (!onComplete) return;
    setCompleteError(null);
    const result = await onComplete(appointment.id, method);
    if (result.ok) {
      setShowCompleteModal(false);
      onClose();
    } else {
      setCompleteError(result.error ?? 'Error al completar el servicio');
    }
  };

  return (
    <>
      <AnimatePresence>
        {appointment && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
              <div
                className="w-full max-w-md rounded-2xl border pointer-events-auto"
                style={{
                  backgroundColor: theme.colors.bgSurface,
                  borderColor: theme.colors.border,
                }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-6 py-5 border-b"
                  style={{ borderColor: theme.colors.border }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-xl"
                      style={{ backgroundColor: theme.colors.primaryMuted }}
                    >
                      <i className="ri-calendar-check-line text-base" style={{ color: theme.colors.primary }} />
                    </div>
                    <div>
                      <h2 className="text-white text-base font-semibold">Detalle de cita</h2>
                      <p className="text-white/40 text-xs mt-0.5">ID: {appointment.id.slice(0, 8)}…</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/40 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5">
                  {/* Client info */}
                  <div
                    className="rounded-xl p-4 border"
                    style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                  >
                    <p className="text-white/35 text-xs uppercase tracking-wider mb-3">Cliente</p>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ backgroundColor: theme.colors.primaryMuted }}
                      >
                        <span className="text-sm font-semibold" style={{ color: theme.colors.primary }}>
                          {appointment.client_name?.charAt(0)?.toUpperCase() ?? '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{appointment.client_name || '—'}</p>
                        <p className="text-white/45 text-xs mt-0.5">{appointment.client_email || '—'}</p>
                        {appointment.client_phone && (
                          <p className="text-white/35 text-xs mt-0.5 flex items-center gap-1">
                            <i className="ri-phone-line text-[10px]" />
                            {appointment.client_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Appointment details */}
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className="rounded-xl p-4 border"
                      style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                    >
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Fecha</p>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-line text-xs" style={{ color: theme.colors.primary }} />
                        </div>
                        <p className="text-white text-sm font-medium">{formatDate(appointment.appointment_date)}</p>
                      </div>
                    </div>

                    <div
                      className="rounded-xl p-4 border"
                      style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                    >
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Horario</p>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-time-line text-xs" style={{ color: theme.colors.primary }} />
                        </div>
                        <p className="text-white text-sm font-medium font-mono">
                          {formatTime(appointment.start_time)} – {formatTime(appointment.end_time)}
                        </p>
                      </div>
                    </div>

                    <div
                      className="rounded-xl p-4 border"
                      style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                    >
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Servicio</p>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-scissors-line text-xs" style={{ color: theme.colors.primary }} />
                        </div>
                        <p className="text-white text-sm font-medium">{appointment.services?.name ?? '—'}</p>
                      </div>
                    </div>

                    <div
                      className="rounded-xl p-4 border"
                      style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                    >
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Estado</p>
                      <StatusBadge status={appointment.status} paymentStatus={appointment.payment_status} />
                    </div>
                  </div>

                  {/* Payment status */}
                  <div
                    className="rounded-xl p-4 border flex items-center justify-between"
                    style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                  >
                    <div>
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-1">Pago</p>
                      <PaymentBadge status={appointment.payment_status} />
                    </div>
                    <div className="text-right">
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-1">Creada</p>
                      <p className="text-white/50 text-xs font-mono">
                        {new Date(appointment.created_at).toLocaleDateString('es-CO', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Error message */}
                  {completeError && (
                    <div className="flex items-center gap-2 rounded-xl px-4 py-3 border border-red-500/20 bg-red-500/8">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-error-warning-line text-sm text-red-400" />
                      </div>
                      <p className="text-red-400 text-xs">{completeError}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  className="flex items-center justify-between px-6 py-4 border-t"
                  style={{ borderColor: theme.colors.border }}
                >
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white hover:bg-white/8 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Cerrar
                  </button>

                  {/* Actions for CONFIRMED status */}
                  {!isFinalStatus && appointment.status === 'confirmed' && (
                    <div className="flex items-center gap-2">
                      {onComplete && (
                        <button
                          onClick={() => setShowCompleteModal(true)}
                          disabled={completing === appointment.id || cancelling === appointment.id}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(16,185,129,0.12)',
                            color: '#34d399',
                            border: '1px solid rgba(16,185,129,0.25)',
                          }}
                        >
                          {completing === appointment.id ? (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Guardando…
                            </>
                          ) : (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-check-double-line text-xs" />
                              </div>
                              Completar servicio
                            </>
                          )}
                        </button>
                      )}

                      {onNoShow && (
                        <button
                          onClick={handleNoShow}
                          disabled={cancelling === appointment.id || completing === appointment.id}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(245,158,11,0.12)',
                            color: '#fbbf24',
                            border: '1px solid rgba(245,158,11,0.25)',
                          }}
                        >
                          {cancelling === appointment.id ? (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Guardando…
                            </>
                          ) : (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-user-unfollow-line text-xs" />
                              </div>
                              No llegó
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Actions for PENDING status */}
                  {appointment.status === 'pending' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Copiar contacto */}
                      <button
                        onClick={handleCopyContact}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          color: copied ? '#34d399' : 'rgba(255,255,255,0.5)',
                          border: '1px solid rgba(255,255,255,0.10)',
                        }}
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${copied ? 'ri-check-line' : 'ri-contacts-line'} text-xs`} />
                        </div>
                        {copied ? 'Copiado' : 'Copiar contacto'}
                      </button>

                      {/* Confirmar manualmente */}
                      {onConfirm && (
                        <button
                          onClick={handleConfirmManual}
                          disabled={confirming || cancelling === appointment.id}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(16,185,129,0.12)',
                            color: '#34d399',
                            border: '1px solid rgba(16,185,129,0.25)',
                          }}
                        >
                          {confirming ? (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Confirmando…
                            </>
                          ) : (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-checkbox-circle-line text-xs" />
                              </div>
                              Confirmar
                            </>
                          )}
                        </button>
                      )}

                      {/* Cancelar */}
                      {onCancel && (
                        <button
                          onClick={async () => { await onCancel(appointment.id); onClose(); }}
                          disabled={cancelling === appointment.id || confirming}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(239,68,68,0.10)',
                            color: '#f87171',
                            border: '1px solid rgba(239,68,68,0.20)',
                          }}
                        >
                          {cancelling === appointment.id ? (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Cancelando…
                            </>
                          ) : (
                            <>
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-close-circle-line text-xs" />
                              </div>
                              Cancelar cita
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Complete service modal — rendered outside the detail modal stack */}
      {showCompleteModal && (
        <CompleteServiceModal
          appointment={appointment}
          completing={completing === appointment.id}
          onConfirm={handleCompleteConfirm}
          onClose={() => { setShowCompleteModal(false); setCompleteError(null); }}
        />
      )}
    </>
  );
}
